import { getPartById } from '../../catalog/part-catalog'
import type { PlacedPart } from '../../domain/assembly'

const METERS_TO_SCENE_UNITS = 20
const CLEARANCE = 0.025

type Bounds = {
  minX: number
  maxX: number
  minY: number
  maxY: number
  minZ: number
  maxZ: number
}

export function getPartBounds(part: PlacedPart): Bounds | null {
  const definition = getPartById(part.partId)
  if (!definition) return null
  const halfX = definition.dimensions[0] * METERS_TO_SCENE_UNITS / 2
  const halfY = definition.dimensions[1] * METERS_TO_SCENE_UNITS / 2
  const halfZ = definition.dimensions[2] * METERS_TO_SCENE_UNITS / 2
  const cosine = Math.abs(Math.cos(part.rotation.y))
  const sine = Math.abs(Math.sin(part.rotation.y))
  const extentX = halfX * cosine + halfZ * sine
  const extentZ = halfX * sine + halfZ * cosine
  return {
    minX: part.position.x - extentX,
    maxX: part.position.x + extentX,
    minY: part.position.y - halfY,
    maxY: part.position.y + halfY,
    minZ: part.position.z - extentZ,
    maxZ: part.position.z + extentZ,
  }
}

function overlaps(a: Bounds, b: Bounds) {
  return a.minX < b.maxX - CLEARANCE
    && a.maxX > b.minX + CLEARANCE
    && a.minY < b.maxY - CLEARANCE
    && a.maxY > b.minY + CLEARANCE
    && a.minZ < b.maxZ - CLEARANCE
    && a.maxZ > b.minZ + CLEARANCE
}

export function findOverlappingPart(movingPart: PlacedPart, parts: PlacedPart[]) {
  const movingBounds = getPartBounds(movingPart)
  if (!movingBounds) return null
  return parts.find((part) => {
    if (part.instanceId === movingPart.instanceId) return false
    const bounds = getPartBounds(part)
    return bounds ? overlaps(movingBounds, bounds) : false
  }) ?? null
}

export function resolveDownwardContact(
  movingPart: PlacedPart,
  parts: PlacedPart[],
  previousY: number,
) {
  if (movingPart.position.y >= previousY) return null
  const obstacle = findOverlappingPart(movingPart, parts)
  const movingBounds = getPartBounds(movingPart)
  const obstacleBounds = obstacle ? getPartBounds(obstacle) : null
  if (!obstacle || !movingBounds || !obstacleBounds) return null
  const bottomOffset = movingPart.position.y - movingBounds.minY
  const restingY = obstacleBounds.maxY + bottomOffset
  if (restingY > previousY + CLEARANCE) return null
  const resolved = {
    ...movingPart,
    position: { ...movingPart.position, y: Number(restingY.toFixed(3)) },
  }
  return findOverlappingPart(resolved, parts) ? null : resolved
}
